import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { 
  AlertTriangle,
  Clock,
  Calendar,
  XCircle
} from 'lucide-react';
import { motion } from 'framer-motion';

interface MissingClockOutDialogProps {
  isOpen: boolean;
  missingClockOutDate?: string;
  clockInAt?: string;
  onSubmit: (clockOutTime: string, reason: string) => Promise<void>;
}

const MissingClockOutDialog: React.FC<MissingClockOutDialogProps> = ({
  isOpen,
  missingClockOutDate,
  clockInAt,
  onSubmit
}) => {
  const [clockOutDate, setClockOutDate] = useState('');
  const [clockOutTime, setClockOutTime] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Initialize date and time when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Set default to yesterday's date if provided, otherwise use yesterday
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      
      const dateStr = missingClockOutDate 
        ? new Date(missingClockOutDate).toISOString().split('T')[0]
        : yesterday.toISOString().split('T')[0];
      
      // Set default time to end of work day (e.g., 6:00 PM)
      const timeStr = '18:00';
      
      setClockOutDate(dateStr);
      setClockOutTime(timeStr);
      setReason('');
      setError('');
    }
  }, [isOpen, missingClockOutDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!clockOutDate || !clockOutTime) {
      setError('Please select both date and time for clock-out');
      return;
    }
    
    if (!reason.trim()) {
      setError('Please provide a reason for the missing clock-out');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Combine date and time into ISO format
      const dateTimeString = `${clockOutDate}T${clockOutTime}:00`;
      const clockOutDateTime = new Date(dateTimeString);
      
      // Convert to ISO string for API
      const clockOutTimeISO = clockOutDateTime.toISOString();
      
      await onSubmit(clockOutTimeISO, reason.trim());
    } catch (err: any) {
      setError(err.message || 'Failed to submit clock-out correction. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const MotionDiv = motion.div;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-[500px]"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <div className="p-2 bg-red-100 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          Missing Clock-Out
        </DialogTitle>

        <form onSubmit={handleSubmit} className="space-y-6">
          <MotionDiv
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="p-4 bg-red-50 border border-red-200 rounded-lg"
          >
            <p className="text-sm text-red-800 font-medium">
              You did not clock out {missingClockOutDate ? `on ${formatDate(missingClockOutDate)}` : 'yesterday'}. 
              Please enter your clock-out time and a reason to continue.
            </p>
            {clockInAt && (
              <p className="text-xs text-red-600 mt-2">
                Clock-in time: {new Date(clockInAt).toLocaleString()}
              </p>
            )}
          </MotionDiv>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="clockOutDate" className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Clock-Out Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="clockOutDate"
                type="date"
                value={clockOutDate}
                onChange={(e) => setClockOutDate(e.target.value)}
                required
                className="w-full"
                max={new Date().toISOString().split('T')[0]} // Cannot be in the future
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="clockOutTime" className="text-sm font-medium flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Clock-Out Time <span className="text-red-500">*</span>
              </Label>
              <Input
                id="clockOutTime"
                type="time"
                value={clockOutTime}
                onChange={(e) => setClockOutTime(e.target.value)}
                required
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason" className="text-sm font-medium">
                Reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Please provide a reason for the missing clock-out..."
                required
                rows={4}
                className="w-full resize-none"
              />
              <p className="text-xs text-gray-500">
                This field is required. Please explain why you forgot to clock out.
              </p>
            </div>
          </div>

          {error && (
            <MotionDiv
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 bg-red-50 border border-red-200 rounded-lg"
            >
              <p className="text-sm text-red-600 flex items-center gap-2">
                <XCircle className="h-4 w-4" />
                {error}
              </p>
            </MotionDiv>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="submit"
              disabled={isSubmitting || !clockOutDate || !clockOutTime || !reason.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSubmitting ? 'Submitting...' : 'Submit & Continue'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MissingClockOutDialog;

